import styles from '../components/video-player.module.scss';

// Youtube API Helpers
// Docs: https://developers.google.com/youtube/iframe_api_reference
export const youTubePlayers = {};
let equalizedVolume = 100;
let equalizedMute = true;

export const createYouTubePlayer = ({ id, analyticsData, muted }) => {
  let played = false;
  let completed = false;

  const player = new window.YT.Player(id, {
    events: {
      onReady: (event) => {
        window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('youTubePlayer.ready', { id, player: event.target });
        if (muted) {
          event.target.mute();
        }
        equalizedMute = muted;
      },
      onStateChange: (event) => {
        if (event.data > -1 && event.data < 3) { // ended, playing, paused
          if (equalizedVolume !== event.target.getVolume() || equalizedMute !== event.target.isMuted()) {
            equalizedVolume = event.target.getVolume();
            equalizedMute = event.target.isMuted();
            window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('video-player.equalize-volume', {
              id,
              volume: equalizedVolume,
              mute: equalizedMute
            });
          }

          const { title: videoName = '', video_id: videoId = '' } = event.target.playerInfo?.videoData || {};
          if (!played && event.data === 1) { // playing
            played = true;
            window.LIFE_CYCLE_EVENT_BUS.trigger('video-player.start', {
              ...(analyticsData || {}),
              videoName,
              videoId
            });
          } else if (!completed && event.data === 0) { // ended
            completed = true;
            window.LIFE_CYCLE_EVENT_BUS.trigger('video-player.complete', {
              ...(analyticsData || {}),
              videoName,
              videoId
            });
          }
        }
      },
    },
  });
  return player;
};

export const initializeYouTubePlayers = ({ analyticsData, muted, id }) => {
  if (!window.onYouTubeIframeAPIReady) {
    window.onYouTubeIframeAPIReady = function () {
      const frames = Array(...document.getElementsByTagName('iframe')).filter(
        (frame) => frame.id && frame.id.includes('thd-youtube-iframe')
      );

      frames.forEach((frame) => {
        if (!youTubePlayers[frame.id]) {
          youTubePlayers[frame.id] = createYouTubePlayer({ id: frame.id, analyticsData, muted });
        }
      });
    };
  }

  document.head.append(
    Object.assign(document.createElement('script'), {
      src: 'https://www.youtube.com/iframe_api',
      id: `${id}_script`,
      onload: /* istanbul ignore next */
      () => {
        const youTubeOnloadInterval = setInterval(() => {
          if (typeof window.YT?.Player !== 'undefined') {
            clearInterval(youTubeOnloadInterval);
            window.onYouTubeIframeAPIReady();
          }
        }, 10);
      }
    })
  );
};

export function destroyYouTubePlayer({ id }) {
  if (youTubePlayers[id]) {
    youTubePlayers[id].destroy();
    delete youTubePlayers[id];
    document.querySelector(`[id="${id}_script"]`).remove();
  }
}

/* Brightcove API Helpers
  Docs: https://player.support.brightcove.com/publish/available-options-standard-iframe-embed-code.html
  PLUGINS REPO
  {
    "name":"plugins",
    "public_url":"http://players.brightcove.net/66036796001/plugins",
    "repo_url":"https://repos.api.brightcove.com/v1/accounts/66036796001/repos/plugins"
  }
    **ALL PLUGINS ARE LIMITED TO ES6 FUNCTIONALITY, PER VIDEOJS DEPENDENCY**

    PLUGINS
    {
      "name":"useIFrameAPIFunctions.js",
      "public_url":"http://players.brightcove.net/66036796001/plugins/useIFrameAPIFunctions.js"
    }

    {
      "name":"setLifeCycleEventTriggers.js",
      "public_url":"http://players.brightcove.net/66036796001/plugins/setLifeCycleEventTriggers.js"
    }
*/
let brightCovePostMessageInitialized = false;
export function triggerBrightCoveLifeCycleEvents(evt, analyticsData) {
  if (evt.data && typeof evt.data === 'object') {
    if (evt.data.triggerName === 'brightCoveReady') {
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('brightCovePlayer.ready', { id: evt.data.uniqueId });
    } else if (evt.data.triggerName === 'brightCoveVolumeChange') {
      if (equalizedVolume !== evt.data.volume || equalizedMute !== evt.data.mute) {
        equalizedVolume = evt.data.volume;
        equalizedMute = evt.data.mute;
        window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('video-player.equalize-volume', {
          id: evt.data.uniqueId,
          volume: equalizedVolume,
          mute: equalizedMute
        });
      }
    } else if (evt.data.triggerName === 'brightCovePlayed') {
      window.LIFE_CYCLE_EVENT_BUS.trigger('video-player.start', {
        ...(analyticsData || {}),
        videoName: evt.data.name,
        videoId: evt.data.id,
      });
    } else if (evt.data.triggerName === 'brightCoveCompleted') {
      window.LIFE_CYCLE_EVENT_BUS.trigger('video-player.complete', {
        ...(analyticsData || {}),
        videoName: evt.data.name,
        videoId: evt.data.id
      });
    }
  }
}

export const initializeBrightCovePlayer = ({ analyticsData, muted }) => {
  if (!brightCovePostMessageInitialized) {
    equalizedMute = muted;
    // Listen for the message, then call triggerBrightCoveLifeCycleEvents() method when received
    window.addEventListener('message', /* istanbul ignore next */(evt) => {
      triggerBrightCoveLifeCycleEvents(evt, analyticsData);
    });
    brightCovePostMessageInitialized = true;
  }
};

// Mixed Helpers
const performActionWhenPlayerIsReady = (props) => {
  if (props.frame) {
    if (props.frame.id.includes('youtube')) {
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.on('youTubePlayer.ready', ({ output }) => {
        if (output.id === props.frame.id) {
          props.ytCallback({ output });
        }
      });
    } else {
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.on('brightCovePlayer.ready', ({ output }) => {
        if (output.id === props.frame.id) {
          props.bcCallback();
        }
      });
    }
  }
};

const callBrightCoveFunction = ({ frame, props }) => {
  frame.contentWindow.postMessage(props, 'https://players.brightcove.net');
};

export const playVideoNoRestart = ({ frame }) => {
  performActionWhenPlayerIsReady({
    frame,
    ytCallback: ({ output }) => {
      if (output.player.getPlayerState() !== 0) { // 0 === ended
        output.player.playVideo();
      }
    },
    bcCallback: () => callBrightCoveFunction({ frame, props: { functionName: 'playVideoNoRestart' } })
  });
};

export const pauseVideo = ({ frame }) => {
  performActionWhenPlayerIsReady({
    frame,
    ytCallback: ({ output }) => { output.player.pauseVideo(); },
    bcCallback: () => callBrightCoveFunction({ frame, props: { functionName: 'pauseVideo' } })
  });
};

export const toggleMuteOnVideo = ({ frame, mute }) => {
  performActionWhenPlayerIsReady({
    frame,
    mute,
    ytCallback: ({ output }) => {
      if (mute) {
        output.player.mute();
      } else {
        output.player.unMute();
      }
    },
    bcCallback: () => callBrightCoveFunction({ frame, props: { functionName: 'setMute', mute } })
  });
};

export const setVolume = ({ frame, volume }) => {
  performActionWhenPlayerIsReady({
    frame,
    ytCallback: ({ output }) => { output.player.setVolume(volume); },
    bcCallback: () => callBrightCoveFunction({ frame, props: { functionName: 'setVolume', volume } })
  });
};

/* IntersectionObserver callback functions */
export const modifyAndReturnObservedEntries = ({
  entries,
  isPlayObserver,
  observedEntries
}) => {
  const originalObservedEntriesLength = observedEntries.length;
  let modifiedObservedEntries = observedEntries;
  entries.forEach((entry) => {
    const entryFrame = {
      target: entry.target,
      isPlayable: entry.isIntersecting
    };

    // Add to observedEntries if current entry is not already in it
    if (!modifiedObservedEntries.filter((observedEntry) => entry.target.id === observedEntry.target.id).length) {
      modifiedObservedEntries.push(entryFrame);
    } else {
      modifiedObservedEntries = observedEntries.map((frameInView) => {
        if (frameInView.target.id === entry.target.id
          // When the entry is no longer 10% into view (isPauseObserver), it should be set to !isPlayable
          // When the entry is 10-90% in view, playability doesn't change
          // When the entry comes 90% into view (isPlayObserver), it should be set to isPlayable
          && entry.isIntersecting === isPlayObserver) {
          return entryFrame;
        }
        return frameInView;
      });
    }
  });

  // Make sure that newly added entries are added in ascending left/right or top/bottom order
  if (modifiedObservedEntries.length !== originalObservedEntriesLength) {
    return modifiedObservedEntries.sort(function (entry1, entry2) {
      const entry1Container = entry1.target.closest(`.${styles['video-player__container']}`);
      const entry2Container = entry2.target.closest(`.${styles['video-player__container']}`);
      if ((entry1Container.offsetLeft > entry2Container.offsetLeft)
      || (entry1Container.offsetTop > entry2Container.offsetTop)) {
        return 1;
      }
      return -1;
    });
  }
  return modifiedObservedEntries;

};

export const getCurrentlyPlayingFrame = ({ checkForNoVideos, observedEntries, currentlyPlayingFrame }) => {
  const playableEntries = observedEntries.filter((observedEntry) => observedEntry.isPlayable === true);
  if (playableEntries.length) {
    const firstEntryInView = playableEntries[0];
    if (firstEntryInView.target !== currentlyPlayingFrame) {
      playVideoNoRestart({ frame: firstEntryInView.target });
      if (currentlyPlayingFrame) {
        pauseVideo({ frame: currentlyPlayingFrame });
      }
      return firstEntryInView.target;
    }
  } else if (checkForNoVideos && currentlyPlayingFrame) { // No videos in Viewport
    pauseVideo({ frame: currentlyPlayingFrame });
    return null;
  }
  return currentlyPlayingFrame;
};

// external helpers
export const pauseAllVideos = () => {
  /* eslint-disable-next-line */
  const videoPlayerIFrames = document.querySelectorAll('iframe[id^="thd-brightcove-iframe-"],iframe[id^="thd-youtube-iframe-"]');
  videoPlayerIFrames.forEach((videoPlayerIFrame) => {
    pauseVideo({ frame: videoPlayerIFrame });
  });
};